import { Session } from '@models/session.model';
import { User } from '@models/user.model';
import SessionService from '@services/session.service';
import {
  Avatar,
  Dropdown,
  Icon,
  usePopper,
  useTranslation,
} from '@testsigma/atoms';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { ProfileDropDownItem } from './profile-dropdown-item';
import { TenantForm } from './tenant-form';

const OFFSET_MODIFIER = [
  {
    name: 'offset',
    options: {
      offset: [0, 12],
    },
  },
];

interface ProfileDropDownPros {
  btnRef;
  session: Session;
  user: User;
  toggleDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  userRole: string;
  isSidebarExpanded: boolean;
}

export const ProfileDropDown: React.FC<ProfileDropDownPros> = ({
  btnRef,
  session,
  user,
  toggleDropdown,
  userRole,
  isSidebarExpanded,
}) => {
  const { t } = useTranslation();
  const [isLogoutInProgress, setIsLogoutInProgress] = useState(false);

  const [dropdownRef, setDropdownRef] = useState(null);
  const [showForm, updateForm] = useState(false);

  const { styles, attributes } = usePopper(btnRef, dropdownRef, {
    placement: 'right-end',
    strategy: 'fixed',
    modifiers: OFFSET_MODIFIER,
  });

  const handleProfile = useCallback(() => {
    window.open(
      `${import.meta.env.VITE_ID_SERVICE_URL}org?view=profile`,
      '_blank',
    );
  }, []);

  const handleChangePassword = useCallback(() => {
    window.open(
      `${import.meta.env.VITE_ID_SERVICE_URL}org?view=changepassword`,
      '_blank',
    );
  }, []);

  const logout = useCallback(() => {
    if (isLogoutInProgress) return;
 
    setIsLogoutInProgress(true);
    SessionService.logout(true).finally(()=>{
      setIsLogoutInProgress(false);
    });
  }, [isLogoutInProgress, setIsLogoutInProgress]);

  const switchAccount = useCallback(() => {
    window.location.href = `${
      import.meta.env.VITE_ID_SERVICE_URL
    }org?signOutURI=`;
  }, []);

  const toggelForm = useCallback(
    (state) => {
      updateForm(state);
    },
    [showForm, updateForm],
  );

  const PROFILE_DROPDOWN_ITEMS = [
    {
      onClick: switchAccount,
      icon: 'sync-alt',
      text: t('left.nav.switch_account_title'),
    },
    {
      onClick: handleChangePassword,
      icon: 'lock',
      text: t('left.nav.change_password'),
    },
    {
      onClick: logout,
      icon: 'logout',
      text: t('left.nav.logout'),

    },
  ];

  // get user avatar url location
  const getSrcUrl = () => {
    if (!session?.user?.identityUserAccountUUId) return null;
    const match = import.meta.env.VITE_ID_SERVICE_URL.match(/^(.+?)\/ui\//);
    const baseUrl = match[1];
    const url = `${baseUrl}/users/${session?.user?.identityUserAccountUUId}/avatar`;
    return url;
  };

  return (
    <>
      <Dropdown
        className={classNames(
          'w-72 z-[1000] transition-all duration-300 ease-in delay-150 p-1.5 !max-h-none',
          {
            '!left-[-150px]': !isSidebarExpanded,
            '!left-2': isSidebarExpanded,
          },
        )}
        toggleDropdown={toggleDropdown}
        setRef={setDropdownRef}
        style={styles.popper}
        attributes={attributes.popper}
        closeOnClickOutside={!showForm}
        buttonRef={btnRef}
      >
        <div className='bg-secondary-50 border border-secondary-200 flex m-3 p-2.5 rounded-xl'>
          <Avatar
            src={getSrcUrl()}
            alt={`${session?.user?.name}`}
            className='!w-16 !h-16 !text-2xl !bg-warning-200 uppercase !border-none'
          />
          <div
            className='flex flex-wrap'
            style={{ width: 'calc(100% - 64px)' }}
          >
            <div className='flex flex-wrap pl-4 overflow-hidden basis-full'>
              <div className='basis-full truncate'>
                <span className='text-unique-600 text-small leading-5 font-medium'>
                  {user?.name}
                </span>
              </div>
              <div className='basis-full truncate py-1 text-secondary-500 text-2xs'>
                {user?.email}
              </div>
            </div>
            <div className='basis-full pl-4 text-secondary-500 text-2xs flex'>
              {user?.userRole || userRole}
              {(user?.isAdmin || user?.isSuperAdmin) && (
                <Icon
                  name='crown'
                  className={classNames(
                    { 'text-unique-300': user.isSuperAdmin && !user.isAdmin },
                    { 'text-warning-300': user.isAdmin },
                    {
                      'text-secondary-400': !(
                        user.isSuperAdmin || user.isAdmin
                      ),
                    },
                    'pl-1 text-small leading-5',
                  )}
                />
              )}
            </div>
          </div>
        </div>
        <ProfileDropDownItem
          key={`profile-dropdown-item-profile`}
          onClick={handleProfile}
          icon={'account-circle'}
          text={t('left.nav.user_profile')}
        />
        <ProfileDropDownItem
          key={`profile-dropdown-item-account`}
          onClick={() => toggelForm(true)}
          icon={'test-development'}
          text={t('left.nav.edit_account')}
        />
        {PROFILE_DROPDOWN_ITEMS.map(({ onClick, icon, text }, index) => (
          <ProfileDropDownItem
            key={`profile-dropdown-item-${index}`}
            onClick={onClick}
            icon={icon}
            text={text}
          />
        ))}
      </Dropdown>
      {showForm && (
        <TenantForm
          session={session}
          isSettingsModalOpen={showForm}
          toggleForm={toggelForm}
        />
      )}
    </>
  );
};
